import { ArrowUp, ArrowDown, MessageCircle, Heart, Image as ImageIcon, Pin, Megaphone, Sparkles, Settings } from 'lucide-react';
import { Discussion } from '@services/discussionService';
import { discussionService } from '@services/discussionService';
import { useAuthStore } from '@store/authStore';
import { formatDate as formatDisplayDate, formatRelativeTime } from '@utils/dateUtils';
import toast from 'react-hot-toast';

interface DiscussionCardProps {
    discussion: Discussion;
    onClick: () => void;
    onVote: (discussionId: string, voteType: 'up' | 'down') => void;
    onPin?: (discussionId: string) => void;
    currentUserId?: string;
}

const TYPE_COLORS = {
    bug: 'red',
    feature: 'yellow',
    question: 'blue',
    feedback: 'green',
    astrology: 'indigo',
    general: 'purple',
    announcement: 'orange',
    system_update: 'cyan',
    feature_update: 'emerald',
};

const getAnnouncementIcon = (type: string) => {
    return type === 'announcement' ? Megaphone : Megaphone;
};

const getPriorityLabel = (priority?: number) => {
    if (!priority) return null;
    if (priority >= 100) return { label: 'URGENT', color: 'red' };
    if (priority >= 50) return { label: 'IMPORTANT', color: 'orange' };
    if (priority >= 10) return { label: 'NORMAL', color: 'blue' };
    return null;
};

const DiscussionCard = ({ discussion, onClick, onVote, onPin, currentUserId }: DiscussionCardProps) => {
    const { isAuthenticated, user } = useAuthStore();
    const types = discussion.types || [discussion.type];  // Use types array or fallback to single type
    const primaryType = types[0] || discussion.type;  // Primary type for background color
    const typeColor = TYPE_COLORS[primaryType] || 'purple';
    const isAdmin = user?.is_admin;
    const isAnnouncement = types.includes('announcement');
    const AnnouncementIcon = isAnnouncement ? getAnnouncementIcon(primaryType) : null;
    const priorityInfo = getPriorityLabel(discussion.priority);

    const hasUpvoted = discussion.upvotes?.includes(currentUserId || '');
    const hasDownvoted = discussion.downvotes?.includes(currentUserId || '');

    const handleVote = (e: React.MouseEvent, voteType: 'up' | 'down') => {
        e.stopPropagation();
        onVote(discussion.id, voteType);
    };

    const handlePin = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onPin) {
            onPin(discussion.id);
        }
    };

    const formatDate = (date: string) => {
        const relative = formatRelativeTime(date);
        if (relative.endsWith('ago') || relative === 'just now') return relative;
        return formatDisplayDate(date);
    };

    return (
        <div
            onClick={onClick}
            className={`bg-white/[0.08] border rounded-3xl p-6 hover:bg-white/[0.12] transition-all cursor-pointer group relative overflow-hidden ${
                isAnnouncement
                    ? `border-${typeColor}-500/50 ring-2 ring-${typeColor}-500/20 bg-gradient-to-br from-${typeColor}-500/5 to-transparent`
                    : discussion.pinned
                    ? 'border-purple-500/50 ring-2 ring-purple-500/20'
                    : 'border-white/20'
            }`}
        >
            <div className={`absolute top-0 right-0 w-64 h-64 bg-${typeColor}-500/5 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 group-hover:bg-${typeColor}-500/10 transition-colors`} />

            <div className="relative z-10">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3 flex-1">
                        {/* User Avatar */}
                        {discussion.user_profile_picture && discussion.user_profile_picture.trim() !== '' ? (
                            <img
                                src={discussion.user_profile_picture}
                                alt={discussion.user_name}
                                className="w-10 h-10 rounded-full border-2 border-white/20 object-cover"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <div
                            className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm"
                            style={{ display: discussion.user_profile_picture && discussion.user_profile_picture.trim() !== '' ? 'none' : 'flex' }}
                        >
                            {discussion.user_name?.[0]?.toUpperCase() || 'U'}
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-white font-bold text-sm font-sans">
                                        {discussion.user_name}
                                    </span>
                                    {types.map((type) => {
                                        const color = TYPE_COLORS[type] || 'purple';
                                        return (
                                            <span key={type} className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-${color}-500/20 text-${color}-300 border border-${color}-500/30`}>
                                                {type}
                                            </span>
                                        );
                                    })}
                                    {discussion.pinned && (
                                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/10 border border-purple-500/30">
                                            <Pin size={12} className="text-purple-400 fill-purple-400" />
                                            <span className="text-[10px] font-bold uppercase text-purple-400">Pinned</span>
                                        </div>
                                    )}
                                    {discussion.admin_hearted && (
                                        <Heart size={14} className="text-red-500 fill-red-500" />
                                    )}
                                    {isAnnouncement && priorityInfo && (
                                        <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full bg-${priorityInfo.color}-500/20 border border-${priorityInfo.color}-500/30 animate-pulse`}>
                                            <span className={`text-[10px] font-bold uppercase text-${priorityInfo.color}-400`}>{priorityInfo.label}</span>
                                        </div>
                                    )}
                                    {isAnnouncement && discussion.category && (
                                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-500/30">
                                            <span className="text-[10px] font-bold uppercase text-indigo-400">{discussion.category}</span>
                                        </div>
                                    )}
                                </div>
                                {discussion.username && (
                                    <span className="text-purple-300/50 text-xs font-sans">
                                        @{discussion.username}
                                    </span>
                                )}
                                <span className="text-purple-300/60 text-xs font-sans">
                                    {formatDate(discussion.created_at)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Admin Pin Button */}
                    {isAdmin && (
                        <button
                            onClick={handlePin}
                            className={`p-2 rounded-lg transition-all ${
                                discussion.pinned
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : 'bg-white/5 hover:bg-white/10 text-white/60'
                            }`}
                            title={discussion.pinned ? 'Unpin discussion' : 'Pin discussion'}
                        >
                            <Pin size={16} className={discussion.pinned ? 'fill-purple-400' : ''} />
                        </button>
                    )}
                </div>

                {/* Title */}
                {discussion.title && (
                    <h3 className="text-xl font-bold text-white mb-2 font-serif line-clamp-2 group-hover:text-purple-300 transition-colors">
                        {discussion.title}
                    </h3>
                )}

                {/* Content Preview */}
                <p className="text-purple-200/70 text-sm mb-4 line-clamp-2 font-sans">
                    {discussion.content}
                </p>

                {/* Image Indicator */}
                {discussion.image_filename && (
                    <div className="flex items-center gap-1.5 text-xs text-purple-400/60 mb-4 font-sans">
                        <ImageIcon size={14} />
                        <span>Has image</span>
                    </div>
                )}

                {/* Footer */}
                <div className="flex items-center gap-4">
                    {/* Voting */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={(e) => handleVote(e, 'up')}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all font-sans text-sm ${
                                hasUpvoted
                                    ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                    : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                        >
                            <ArrowUp size={14} />
                            <span>{discussion.upvote_count}</span>
                        </button>

                        <button
                            onClick={(e) => handleVote(e, 'down')}
                            disabled={!isAuthenticated}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all font-sans text-sm ${
                                hasDownvoted
                                    ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                    : 'bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed'
                            }`}
                        >
                            <ArrowDown size={14} />
                            <span>{discussion.downvote_count}</span>
                        </button>
                    </div>

                    {/* Comments Count */}
                    <div className="flex items-center gap-1.5 text-white/60 font-sans text-sm">
                        <MessageCircle size={14} />
                        <span>{discussion.comment_count} {discussion.comment_count === 1 ? 'comment' : 'comments'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiscussionCard;
